import React, { FC } from "react";
import { Button, Modal, Select, Spin, Tooltip } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { ProposalExportPreviewModalBody } from "./modal-body";
import { Brand, MSWordFont, NumberedListFormat, Proposal } from "../../data-types";
import styles from "./modal.css";
import { TaxLabel } from "~/gql/graphql";
import { isNumber } from "../../data-types";

import { validateAll } from "@virtual-pricing-director/vpd-illustration";
import { VPDLabel } from "~/context/taxonomy-context";

const {
  ["body-container"]: bodyContainerClassName,
  // ["proposal-customisation-selectors"]: proposalCustomisationSelectors,
  ["select"]: selectClassName,
  ["select-group"]: selectGroupClassName,
} = styles as any;

export type ProposalExportPreviewModalProps = {
  show: boolean;
  loading: boolean;
  logo: string;
  footer: string;
  downloading: boolean;
  proposal?: Proposal;
  legalFees?: number | { lower: number; upper: number };
  onCancel: () => void;
  onDownload: () => void;
  onEdit: (matterEditorChild: string) => void;
  taxLevelPercent: number;
  fontFamily: MSWordFont;
  numberedListFormat: NumberedListFormat;
  lineBreakInLists: boolean;
  brands: Brand[];
  selectedBrand: number;
  selectBrand: (brandId: number) => Promise<void>;
  includeTasksInProposalOutput: boolean;
  includeRatesInProposalOutput: boolean;
  includeFeeEarnersInProposalOutput: boolean;
  setIncludeRatesInProposalOutputAtMatterLevel: (include: boolean) => Promise<void>;
  setIncludeTasksInProposalOutputAtMatterLevel: (include: boolean) => Promise<void>;
  setIncludeFeeEarnersInProposalOutputAtMatterLevel: (include: boolean) => Promise<void>;
  currencySymbol: string;
  taxLabel: TaxLabel;
  disableTaskToggle?: boolean;
  feeRiskAllowed?: boolean;
  handleFeeRiskIncludeChange?: (include: boolean) => void;
};

const ProposalPreviewTitle: FC<{
  brands: Brand[];
  selectedBrand: number;
  selectBrand: (brandId: number) => Promise<void>;
  includeTasksInProposalOutput: boolean;
  includeRatesInProposalOutput: boolean;
  includeFeeEarnersInProposalOutput: boolean;
  setIncludeRatesInProposalOutputAtMatterLevel: (include: boolean) => Promise<void>;
  setIncludeTasksInProposalOutputAtMatterLevel: (include: boolean) => Promise<void>;
  setIncludeFeeEarnersInProposalOutputAtMatterLevel: (include: boolean) => Promise<void>;
  disableTaskToggle?: boolean;
  disableRateToggle?: boolean;
  showHideRates: boolean;
  feeRiskAllowed?: boolean;
  feeRiskValid?: boolean;
  handleFeeRiskIncludeChange?: (include: boolean) => void;
  includeFeeRiskInProposalOutput?: boolean;
}> = ({
  brands,
  selectedBrand,
  selectBrand,
  includeTasksInProposalOutput,
  includeRatesInProposalOutput,
  includeFeeEarnersInProposalOutput,
  setIncludeTasksInProposalOutputAtMatterLevel,
  setIncludeRatesInProposalOutputAtMatterLevel,
  setIncludeFeeEarnersInProposalOutputAtMatterLevel,
  disableRateToggle = false,
  disableTaskToggle,
  showHideRates,
  feeRiskAllowed = false,
  feeRiskValid = false,
  handleFeeRiskIncludeChange,
  includeFeeRiskInProposalOutput = false,
}) => {
  return (
    <div className={styles["header-container"]}>
      <span className={selectGroupClassName} style={{ marginRight: "48px" }}>
        Proposal Preview
      </span>
      {brands.length > 0 && (
        <div className={selectGroupClassName}>
          <span>Brand:</span>
          <Select
            className={selectClassName}
            options={brands.map((b) => {
              return {
                value: b.id,
                label: b.organisationName,
              };
            })}
            placeholder="Select Proposal Brand"
            onSelect={(_, option) => selectBrand(option.value)}
            defaultValue={selectedBrand || brands.find((b) => b.isDefault)?.id || undefined}
          />
        </div>
      )}
      <div className={selectGroupClassName}>
        <span>Include tasks</span>
        <Select
          className={selectClassName}
          options={[
            {
              value: "yes",
              label: "Yes",
            },
            {
              value: "no",
              label: "No",
            },
          ]}
          onSelect={(_, optionRaw) => {
            const option = "yes" === optionRaw.value ? true : false;
            setIncludeTasksInProposalOutputAtMatterLevel(option);
          }}
          placeholder="Yes/No"
          defaultValue={includeTasksInProposalOutput === true ? "yes" : "no"}
          disabled={disableTaskToggle}
        />
      </div>
      {showHideRates && (
        <>
          <div className={selectGroupClassName}>
            <span>Show {VPDLabel("FeeEarner")}s</span>
            <Select
              className={selectClassName}
              options={[
                {
                  value: "yes",
                  label: "Yes",
                },
                {
                  value: "no",
                  label: "No",
                },
              ]}
              onSelect={(_, optionRaw) => {
                const option = "yes" === optionRaw.value ? true : false;
                setIncludeFeeEarnersInProposalOutputAtMatterLevel(option);
              }}
              placeholder="Yes/No"
              defaultValue={includeFeeEarnersInProposalOutput === true ? "yes" : "no"}
            />
          </div>
          <div className={selectGroupClassName}>
            <span>Show Rates</span>
            <Select
              className={selectClassName}
              options={[
                {
                  value: "yes",
                  label: "Yes",
                },
                {
                  value: "no",
                  label: "No",
                },
              ]}
              onSelect={(_, optionRaw) => {
                const option = "yes" === optionRaw.value ? true : false;
                setIncludeRatesInProposalOutputAtMatterLevel(option);
              }}
              placeholder="Yes/No"
              defaultValue={includeRatesInProposalOutput === true ? "yes" : "no"}
              disabled={disableRateToggle}
            />
          </div>
        </>
      )}
      {feeRiskAllowed && handleFeeRiskIncludeChange && (
        <div className={selectGroupClassName}>
          <span>Include Diagram</span>
          <Tooltip
            placement="bottom"
            title={
              feeRiskValid
                ? ""
                : "Ensure all fields on Fee Risk Profile tab are complete to include diagram"
            }
          >
            <Select
              className={selectClassName}
              options={[
                {
                  value: "yes",
                  label: "Yes",
                },
                {
                  value: "no",
                  label: "No",
                },
              ]}
              onSelect={(_, optionRaw) => {
                const option = "yes" === optionRaw.value ? true : false;
                handleFeeRiskIncludeChange(option);
              }}
              placeholder="Yes/No"
              value={
                includeFeeRiskInProposalOutput === true && feeRiskValid === true ? "yes" : "no"
              }
              disabled={!feeRiskValid}
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export const ProposalExportPreviewModal: FC<ProposalExportPreviewModalProps> = ({
  show,
  loading,
  logo,
  footer,
  downloading,
  proposal,
  legalFees,
  onCancel,
  onDownload,
  onEdit,
  fontFamily,
  brands,
  selectedBrand,
  selectBrand,
  includeTasksInProposalOutput,
  includeRatesInProposalOutput,
  includeFeeEarnersInProposalOutput,
  setIncludeRatesInProposalOutputAtMatterLevel,
  setIncludeTasksInProposalOutputAtMatterLevel,
  setIncludeFeeEarnersInProposalOutputAtMatterLevel,
  currencySymbol,
  taxLabel,
  disableTaskToggle,
  numberedListFormat,
  lineBreakInLists,
  feeRiskAllowed,
  handleFeeRiskIncludeChange,
}) => {
  let feeRiskValid;
  if (legalFees && !isNumber(legalFees)) {
    feeRiskValid = proposal
      ? validateAll({
          lowestAnticipatedFee: legalFees.lower,
          highestAnticipatedFee: legalFees.upper,
          lowestFee: proposal.priceOptions.feeRiskLower,
          highestFee: proposal.priceOptions.feeRiskUpper,
          fixedFee: proposal.priceOptions.fixedChosen,
          cappedFee: proposal.priceOptions.cappedChosen,
        })
      : false;
  } else {
    feeRiskValid = false;
  }

  return (
    <Modal
      title={
        <ProposalPreviewTitle
          showHideRates={true}
          brands={brands}
          selectedBrand={selectedBrand}
          selectBrand={selectBrand}
          includeRatesInProposalOutput={includeRatesInProposalOutput}
          includeTasksInProposalOutput={includeTasksInProposalOutput}
          includeFeeEarnersInProposalOutput={includeFeeEarnersInProposalOutput}
          setIncludeRatesInProposalOutputAtMatterLevel={
            setIncludeRatesInProposalOutputAtMatterLevel
          }
          setIncludeTasksInProposalOutputAtMatterLevel={
            setIncludeTasksInProposalOutputAtMatterLevel
          }
          setIncludeFeeEarnersInProposalOutputAtMatterLevel={
            setIncludeFeeEarnersInProposalOutputAtMatterLevel
          }
          disableTaskToggle={disableTaskToggle}
          disableRateToggle={!includeFeeEarnersInProposalOutput}
          feeRiskAllowed={feeRiskAllowed}
          feeRiskValid={feeRiskValid}
          handleFeeRiskIncludeChange={handleFeeRiskIncludeChange}
          includeFeeRiskInProposalOutput={proposal?.feeRiskInclude}
        />
      }
      open={show}
      width="60%"
      style={{ top: "36px" }}
      onCancel={onCancel}
      bodyStyle={{
        maxHeight: "75vh",
        overflowY: "scroll",
        backgroundColor: "gray",
      }}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="download"
          type="primary"
          icon={<DownloadOutlined />}
          loading={downloading}
          disabled={downloading}
          onClick={onDownload}
        >
          Download
        </Button>,
      ]}
    >
      <div className={bodyContainerClassName} style={{ fontFamily }}>
        {loading ? (
          <Spin size="large" tip="Loading..." />
        ) : (
          proposal && (
            <ProposalExportPreviewModalBody
              numberedListFormat={numberedListFormat}
              lineBreakInLists={lineBreakInLists}
              showFeeEarners={includeFeeEarnersInProposalOutput}
              showRates={includeRatesInProposalOutput}
              logo={logo}
              footer={footer}
              onClickEdit={onEdit}
              proposal={proposal}
              legalFees={legalFees}
              currencySymbol={currencySymbol}
              taxLabel={taxLabel}
              feeRiskValid={feeRiskValid}
            />
          )
        )}
      </div>
    </Modal>
  );
};
